import BaseCard from '../../../Prototype/Level/Card/BaseCard';
import CardComponent from '../../../Component/Level/Card';
import TransformComponent from '../../../Component/Transform';
import CardSystem, { getCardHandler } from '../CardSystem';
import ITask from '../../../Utility/Task/ITask';
import { delay } from '../../../Utility/Task/TaskQueue';
import AnimationComponent from '../../../Component/Animation';
import { LevelActions } from '../../../State/Level/LevelActions';
import { StartAnimation } from '../../Animation/AnimationHelper';
import { AUDIO_CARD_DEAL_CARDS, AUDIO_LEVEL_INTRO_CARDS } from '../../Audio';
import { AudioTrackType } from '../../Audio/AudioClip';
import { StartAudio } from '../../Audio/AudioHelper';
import { MathHelper } from '../../../Utility/MathHelper';

export default class LevelIntroTask implements ITask<CardSystem> {
    public *execute(system: CardSystem): IterableIterator<number> {
        const cardsInPlay = system.getCardsInPlay();
        const cardsInStack = system.getCardsInStack();

        // Set initial card positions.
        cardsInPlay.forEach(card => {
            system.cardsContainer.addChild(card);

            const transform = card.getComponent(TransformComponent);
            const cardComponent = card.getComponent(CardComponent);
            transform.x = cardComponent.x;
            transform.y = cardComponent.y - 1024;
            transform.rotation = cardComponent.rotation - Math.PI * 0.25;

            getCardHandler(card).setCardFaceUp(false);
        });

        cardsInStack.forEach(card => {
            system.stackContainer.addChild(card);

            const transform = card.getComponent(TransformComponent);
            transform.x = -200;
            transform.y = 200;
            transform.rotation = 0;
        });

        // First sort cardsInPlay for animation sequencing.
        cardsInPlay.sort((cardA: BaseCard, cardB: BaseCard) => {
            const cardAComponent = cardA.getComponent(CardComponent);
            const cardBComponent = cardB.getComponent(CardComponent);
            return cardAComponent.sequence - cardBComponent.sequence;
        });

        StartAudio(system.scene, AUDIO_LEVEL_INTRO_CARDS, AudioTrackType.SFX);

        // Get the delay (ms) between each card animation based on the number of cards in play.
        const playDelay = MathHelper.clamp(1000 / cardsInPlay.length, 10, 50);
        for (const card of cardsInPlay) {
            const cardComponent = card.getComponent(CardComponent);
            StartAnimation(card, 'CardFallIn', {
                x: cardComponent.x,
                y: cardComponent.y,
                rotation: cardComponent.rotation,
            });

            // Delay between each card sliding in.
            yield* delay(playDelay);
        }

        yield* this.waitForAnimations(cardsInPlay);

        StartAudio(system.scene, AUDIO_CARD_DEAL_CARDS, AudioTrackType.SFX);

        // Get the delay (ms) between each card animation based on the number of cards in stack.
        const stackDelay = MathHelper.clamp(1000 / cardsInStack.length, 10, 50);
        for (const card of cardsInStack) {
            StartAnimation(card, 'CardSlide', {
                x: -150,
                y: 0,
            });

            yield* delay(stackDelay);
        }

        yield* delay(100);

        // Get cards that are to be revealed after being dealt to the board.
        const cardsLeftToRight = [];
        for (const card of cardsInPlay) {
            const cardComponent = card.getComponent(CardComponent);
            if (cardComponent.faceUp) {
                cardsLeftToRight.push(card);
            }
        }

        // Sort cards from left to right for consistent reveal animation.
        cardsLeftToRight.sort((cardA: BaseCard, cardB: BaseCard) => {
            const cardATransform = cardA.getComponent(TransformComponent);
            const cardBTransform = cardB.getComponent(TransformComponent);
            return cardATransform.x - cardBTransform.x;
        });

        // Get the delay (ms) between each card animation based on the number of cards to flip.
        // prettier-ignore
        const flipDelay = MathHelper.clamp(2000 / cardsLeftToRight.length, 50, 200);
        for (const card of cardsLeftToRight) {
            StartAnimation(card, 'CardFlip', { faceUp: true });

            // Wait between each card flip.
            yield* delay(flipDelay);
        }

        yield* this.waitForAnimations(cardsLeftToRight);

        system.state.dispatch(LevelActions.levelActive());

        // Play top card is stack.
        const inStack = system.state.getState().levelState.cardsState.inStack;
        if (cardsInStack.length === 0) return;
        const topCardID = inStack[cardsInStack.length - 1];
        system.playCard(topCardID);
        //
    }

    private *waitForAnimations(cards: BaseCard[]): IterableIterator<number> {
        // Wait for animations.
        for (const card of cards) {
            const animation = card.getComponent(AnimationComponent);
            while (animation.isPlaying) {
                yield;
            }
        }
    }
}
